import { isPlatformWeb } from '@24i/nxg-core-utils';
import {
    Asset,
    Edition,
    Stream,
    Subtitle,
    SubtitleSource,
    TimelineEvent,
} from '@24i/nxg-sdk-photon';
import {
    DRM_TYPE,
    ISource,
    ISourceDrm,
    ISourceMetadata,
    ISourcePoster,
    ISourceSsai,
    ISourceSsaiAwsMediaTailor,
    ISourceSsaiGoogleDai,
    ISourceTextTrack,
    ISourceThumbnailSpritesheet,
    ISourceTimelineEvent,
    ISourceTimelineEventType,
    MIME_TYPE,
    TrackKind,
    TrackType,
} from '@24i/player-base';

export const mapToPlayerMimetype = (type: string): MIME_TYPE => {
    switch (type) {
        case 'dash':
            return MIME_TYPE.DASH;
        case 'hls':
            return MIME_TYPE.HLS;
        case 'mp3':
            return MIME_TYPE.MP3_AUDIO;
        case 'mp4':
            return MIME_TYPE.MP4_VIDEO;
        case 'webvtt':
        case 'vtt':
            return MIME_TYPE.WEBVTT;
        case 'srt':
            return MIME_TYPE.SUBRIP;
        case 'ttml':
            return MIME_TYPE.TTML;
        case 'xml':
        default:
            return type as MIME_TYPE;
    }
};

export const mapToDrmPackageName = (drmType?: string): DRM_TYPE | undefined => {
    switch (drmType) {
        case 'widevine':
            return DRM_TYPE.WIDEVINE;
        case 'fairplay':
            return DRM_TYPE.FAIRPLAY;
        case 'playready':
            return DRM_TYPE.PLAYREADY;
        case 'unencrypted':
        case undefined:
        case null:
            return undefined;
        default:
            return drmType as DRM_TYPE;
    }
};

const mapToSourceDrm = (stream: Stream): ISourceDrm | undefined => {
    const packageName = mapToDrmPackageName(stream.drm);
    const { licenseUrl, certificateUrl } = stream.data || {};
    if (!packageName || !licenseUrl) return undefined;
    return {
        packageName,
        licenseServer: licenseUrl,
        certificateUrl: certificateUrl || undefined,
    };
};

const mapToSourceAds = (stream: Stream): string | undefined => {
    return stream.data?.adsUrl || stream.advertisement || undefined;
};

const mapToSouceTimelineEventType = (type: string): ISourceTimelineEventType => {
    switch (type) {
        case 'recap':
            return ISourceTimelineEventType.RECAP;
        case 'intro':
            return ISourceTimelineEventType.INTRO;
        case 'credits':
            return ISourceTimelineEventType.CREDITS;
        default:
            return type as ISourceTimelineEventType;
    }
};

export const mapToSourceTimelineEvent = (timelineEvent: TimelineEvent): ISourceTimelineEvent => {
    const { type, name, start, end } = timelineEvent;
    return {
        type: mapToSouceTimelineEventType(type),
        name,
        start: start * 1000,
        end: end * 1000,
    };
};

// Order on the array indicates preference, so it is important
export const supportedSubtitleFormats: string[] = isPlatformWeb
    ? [MIME_TYPE.WEBVTT, MIME_TYPE.TTML]
    : [MIME_TYPE.WEBVTT];

type SubtitleTrackWithUrlAndMimetype = Pick<ISourceTextTrack, 'url' | 'mimeType'>;
const filterUnsupportedTracksAndMapValidOnes = (
    filteredSources: SubtitleTrackWithUrlAndMimetype[],
    source: SubtitleSource
): SubtitleTrackWithUrlAndMimetype[] => {
    // Filter empty
    if (source?.src) {
        const mappedType = mapToPlayerMimetype(source.type);
        // Filter unsupported
        if (supportedSubtitleFormats.includes(mappedType)) {
            const mappedSource: ISourceTextTrack = {
                url: source.src,
                mimeType: mappedType,
            };
            filteredSources.push(mappedSource);
        }
    }
    return filteredSources;
};

export const mapToSourceTextTracks = (subtitles?: Subtitle[] | null): ISourceTextTrack[] => {
    const hasExternalSubtitles = subtitles?.length;
    if (hasExternalSubtitles) {
        return subtitles.reduce<ISourceTextTrack[]>(
            (filteredSubtitles: ISourceTextTrack[], subtitle: Subtitle) => {
                const subtitleHasSources = subtitle?.sources?.length || false;
                if (subtitleHasSources) {
                    const newSources = subtitle.sources.reduce<
                        Pick<ISourceTextTrack, 'url' | 'mimeType'>[]
                    >(filterUnsupportedTracksAndMapValidOnes, []);

                    if (newSources.length) {
                        // Sort them by preference
                        newSources.sort(
                            (a, b) =>
                                supportedSubtitleFormats.indexOf(a.mimeType || '') -
                                supportedSubtitleFormats.indexOf(b.mimeType || '')
                        );

                        // Pick the first in order of preference
                        const {
                            label,
                            language,
                            // @ts-ignore TODO: was here, but is not documented on backstage
                            default: isDefault = false,
                            forced = false,
                        } = subtitle;

                        const newTrack: ISourceTextTrack = {
                            id: label,
                            label,
                            language,
                            default: isDefault,
                            selected: false,
                            forced,
                            kind: TrackKind.Subtitles,
                            type: TrackType.Text,
                            ...newSources[0], // get mimeType and url
                        };

                        filteredSubtitles.push(newTrack);
                    }
                }
                return filteredSubtitles;
            },
            []
        );
    }
    return [];
};

const mapToSourceThumbnails = (stream: Stream): ISourceThumbnailSpritesheet | undefined => {
    if (stream?.thumbnails?.type === 'spritesheet') {
        const thumbnailsData = stream.thumbnails.data;
        if (!thumbnailsData) return undefined;
        let { interval } = thumbnailsData;
        if (typeof interval === 'number') {
            // convert to milliseconds
            interval *= 1000;
        } else if (interval instanceof Array) {
            // convert to milliseconds
            interval = interval.map((time) => time * 1000);
        }

        let { image } = thumbnailsData;
        if (!image) return undefined;
        if (image.startsWith('//')) image = `https:${image}`;

        return {
            image,
            frame: thumbnailsData.frame,
            orientation: thumbnailsData.orientation,
            gridCols: thumbnailsData.gridCols,
            interval,
        } as ISourceThumbnailSpritesheet;
    }
    return undefined;
};

const mapToGoogleSsaiFromUrl = (
    url: string | null | undefined
): ISourceSsaiGoogleDai | undefined => {
    if (!url || !url.startsWith('https://dai.google.com/?')) return undefined;
    /* NOTE: If there is a standard way of parsing URL parameters that works in React Native
      (because URL & URLSearchParams are broken in RN), please, please, please do suggest
      those in PR review (or replace it here afterwards). */
    const parameters = url
        .split('?')[1]
        .split('&')
        .reduce((acc, queryPart) => {
            const [key, encodedValue] = queryPart.split('=');
            return {
                ...acc,
                [key]: decodeURIComponent(encodedValue),
            };
        }, {} as Record<string, string>);
    return {
        type: 'googledai',
        live: !!parameters.assetKey,
        assetKey: parameters.assetKey,
        contentSourceId: parameters.contentSourceId,
        videoId: parameters.videoId,
        apiKey: parameters.apiKey,
    };
};

const mapToGoogleSsaiFromData = (parameters: Stream['data']): ISourceSsaiGoogleDai | undefined => {
    const { cmsid, vid, event, authToken } = parameters ?? {};
    const isDaiLive = Boolean(event);
    const isDaiVod = Boolean(cmsid && vid);
    if (!isDaiLive && !isDaiVod) return undefined;
    return {
        type: 'googledai',
        live: isDaiLive,
        assetKey: event || undefined,
        contentSourceId: cmsid || undefined,
        videoId: vid || undefined,
        authToken: authToken || undefined,
    };
};

const mapToMediaTailorSsaiFromUrl = (
    url: string | null | undefined,
    preferServerSideTracking: boolean
): ISourceSsaiAwsMediaTailor | undefined => {
    if (!url) return undefined;
    const match = url.match(
        /^(https?:\/\/.+\.mediatailor\.[^.]+\.amazonaws.com\/v1)\/(session|master|dash)\/(.*)$/
    );
    if (!match) return undefined;
    if (preferServerSideTracking && match[2] !== 'session') return undefined;
    return {
        type: 'awsmediatailor',
        sessionUrl: `${match[1]}/session/${match[3]}`,
    };
};

const mapToMediaTailorSsaiFromData = (
    parameters: Stream['data']
): ISourceSsaiAwsMediaTailor | undefined => {
    const { sessionUrl, adsParams } = parameters ?? {};
    if (!sessionUrl) return undefined;
    return {
        type: 'awsmediatailor',
        sessionUrl,
        adsParams: adsParams || undefined,
    };
};

const mapToSourceSsai = (stream: Stream): ISourceSsai | undefined => {
    let ssai: ISourceSsai | undefined;
    ssai = ssai ?? mapToGoogleSsaiFromData(stream.data);
    ssai = ssai ?? mapToMediaTailorSsaiFromData(stream.data);
    ssai = ssai ?? mapToGoogleSsaiFromUrl(stream.url);
    ssai = ssai ?? mapToMediaTailorSsaiFromUrl(stream.url, true);
    return ssai;
};

const mapToSourcePosters = (asset: Asset): ISourcePoster[] => {
    const posters: ISourcePoster[] = [];
    if (asset.still) posters.push({ url: asset.still });
    if (asset.background) posters.push({ url: asset.background });
    if (asset.poster) posters.push({ url: asset.poster });
    return posters;
};

const MAX_VALID_PROGRAM_DURATION = 86400000;

const mapToSourceMetadata = (asset: Asset): ISourceMetadata => {
    const programStart = (asset?.startsAt || 0) * 1000 || undefined;
    const programEnd = (asset?.endsAt || 0) * 1000 || undefined;
    const isStartEndInvalid =
        programStart && programEnd && programEnd - programStart > MAX_VALID_PROGRAM_DURATION;
    return {
        title: asset?.title || '',
        description: asset?.subtitle || '',
        programStart: isStartEndInvalid ? undefined : programStart,
        programEnd: isStartEndInvalid ? undefined : programEnd,
    };
};

export const buildPlayerSource = (stream: Stream, edition: Edition, asset: Asset): ISource => {
    let url = stream.url || undefined;
    const mimeType = mapToPlayerMimetype(stream.type);
    const drm = mapToSourceDrm(stream);
    const ads = mapToSourceAds(stream);
    const ssai = mapToSourceSsai(stream);
    if (ssai) {
        url = undefined;
    }
    const textTracks = mapToSourceTextTracks(stream.subtitles || edition.subtitles);
    const timeline = edition.timeline?.map(mapToSourceTimelineEvent);
    const thumbnails = mapToSourceThumbnails(stream);
    const posters = mapToSourcePosters(asset);
    const metadata = mapToSourceMetadata(asset);

    return {
        url,
        mimeType,
        drm,
        ads,
        ssai,
        textTracks,
        timeline,
        thumbnails,
        posters,
        metadata,
    };
};
